import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { useAppSelector } from 'app/hooks';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { ReferentialKey } from 'api/referential';
import { quotesAPI } from 'services/swagger-api';
import {
  useQuoteContractualizationData,
  useQuoteMandateData,
  useQuoteMandateLoading,
  useQuoteMandateError,
  useQuoteMandateActions
} from '../../store';
import {
  QuoteMandateFormValues,
  createQuoteMandateFormConfig
} from './quoteMandateForm';
import {
  mapResponseToFormValues,
  mapFormValuesToCommand,
  getSelectedBankAccountId
} from './quoteMandateMappers';

interface UseQuoteMandateFormProps {
  onSuccess?: () => void;
}

export const useQuoteMandateForm = ({ onSuccess }: UseQuoteMandateFormProps) => {
  const quoteData = useQuoteContractualizationData();
  const formikRef = useRef<FormikProps<QuoteMandateFormValues>>(null);
  const quoteMandate = useQuoteMandateData();
  const isFetchingMandate = useQuoteMandateLoading();
  const fetchMandateError = useQuoteMandateError();
  const { fetchQuoteMandate, reset } = useQuoteMandateActions();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setPanelActions, closePanel } = usePanel();

  // Loader tant que le mandat n'est pas en store : couvre aussi la frame entre le
  // reset du mount et le passage de loading à true par fetchQuoteMandate.
  const isLoading =
    isSubmitting ||
    isFetchingMandate ||
    (!quoteMandate && !fetchMandateError && !!quoteData?.insured.id);

  // Un aliment dont le collectif règle pour tous n'a pas de compte à saisir : la
  // validation s'adapte pour ne pas bloquer sur un champ masqué.
  const requiresOwnMandate = quoteMandate?.requiresOwnMandate ?? true;
  const formConfig = useMemo(
    () => createQuoteMandateFormConfig(requiresOwnMandate),
    [requiresOwnMandate]
  );

  const frequencyReferential = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.MandateFrequency)
  );

  // Reset store on mount and fetch data
  useEffect(() => {
    reset();
    if (quoteData?.insured.id) {
      fetchQuoteMandate(quoteData.quoteId);
    }
    return () => reset();
  }, [quoteData, fetchQuoteMandate, reset]);

  // Panel actions
  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: !isFormValid,
        onSubmit: () => formikRef.current?.submitForm()
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const selectedBankAccountId = useMemo(
    () => getSelectedBankAccountId(quoteMandate),
    [quoteMandate]
  );

  const initialValues = useMemo(
    () => mapResponseToFormValues(quoteMandate, selectedBankAccountId),
    [quoteMandate, selectedBankAccountId]
  );

  const onSubmit = useCallback(
    async (values: QuoteMandateFormValues) => {
      const frequencyId =
        frequencyReferential.find((ref) => ref.code === values.frequency)?.id ||
        '';

      if (!quoteData?.insured.id) return;

      setIsSubmitting(true);
      try {
        const command = mapFormValuesToCommand(values, frequencyId);
        await quotesAPI.addScheduleToMandate({
          quoteId: quoteData.quoteId,
          updateQuoteMandateDto: command
        });
        onSuccess?.();
      } catch (error) {
        console.error('Error on submit mandate:', error);
      } finally {
        setIsSubmitting(false);
        closePanel();
      }
    },
    [quoteData, frequencyReferential, onSuccess, closePanel]
  );

  return {
    formikRef,
    initialValues,
    validationSchema: formConfig.validationSchema,
    isLoading,
    isFormValid,
    setIsFormValid,
    onSubmit,
    formKey: quoteMandate?.startDate?.toString()
  };
};
